import { io } from 'socket.io-client';

class SocketService {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.listeners = new Map();
    }

    connect(userId, userType = 'user') {
        // Same user: reuse the existing socket (even while it is still connecting) so handlers aren't attached twice
        if (this.socket && this.currentUserId === userId) {
            if (!this.socket.connected && !this.socket.active) this.socket.connect();
            return this.socket;
        }

        // Disconnect existing connection if connecting as different user
        if (this.socket && this.currentUserId && this.currentUserId !== userId) {
            console.log('🔌 Disconnecting previous socket for different user');
            this.disconnect();
        }

        // Determine the correct URL for Socket.IO connection
        const isDevelopment = import.meta.env.DEV;
        const socketUrl = isDevelopment
            ? 'http://localhost:5000'
            : 'https://dbc.nordic3d.us';

        // Connect to the backend
        this.socket = io(socketUrl, {
            withCredentials: true,
            autoConnect: true,
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
            timeout: 20000,
            transports: ['websocket', 'polling'],
            forceNew: false,
            upgrade: true
        });

        // Store current user ID
        this.currentUserId = userId;

        // Handle connection
        this.socket.on('connect', () => {
            this.isConnected = true;
            // Authenticate with the server
            this.socket.emit('authenticate', { userId, userType });
        });

        // Handle authentication response
        this.socket.on('authenticated', (data) => {
            if (data.success) {
                this.emit('socketReady', data);
            }
        });

        // Handle disconnection
        this.socket.on('disconnect', (reason) => {
            this.isConnected = false;
            this.emit('socketDisconnected', reason);
        });

        // Handle reconnection
        this.socket.on('reconnect', () => {
            this.isConnected = true;
            // Re-authenticate on reconnection
            if (userId) {
                this.socket.emit('authenticate', { userId, userType });
            }
        });

        // Handle real-time card views
        this.socket.on('cardViewed', (data) => {
            this.emit('cardViewed', data);
        });

        // Someone shared their contact info from the owner's card
        this.socket.on('contactExchange', (data) => {
            this.emit('contactExchange', data);
        });

        // Handle live analytics
        this.socket.on('liveAnalytics', (data) => {
            this.emit('liveAnalytics', data);
        });

        // Handle global card views (for admin)
        this.socket.on('globalCardView', (data) => {
            this.emit('globalCardView', data);
        });

        // Handle user status changes (for admin)
        this.socket.on('userStatusChange', (data) => {
            this.emit('userStatusChange', data);
        });

        // Handle errors
        this.socket.on('connect_error', (error) => {
            this.emit('socketError', error);
        });

        return this.socket;
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
            this.currentUserId = null;
        }
    }

    // Event emitter functionality
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    off(event, callback) {
        if (!this.listeners.has(event)) return;
        const callbacks = this.listeners.get(event);
        const index = callbacks.indexOf(callback);
        if (index > -1) {
            callbacks.splice(index, 1);
        }
    }

    emit(event, data) {
        if (!this.listeners.has(event)) return;
        this.listeners.get(event).forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                console.error('Error in socket event callback:', error);
            }
        });
    }

    // Convenience methods
    requestLiveAnalytics() {
        if (this.socket && this.isConnected) {
            this.socket.emit('requestLiveAnalytics');
        }
    }

    joinUserRoom(userId) {
        if (this.socket && this.isConnected) {
            this.socket.emit('userOnline', userId);
        }
    }

    // Admin methods
    subscribeToGlobalEvents() {
        if (this.socket && this.isConnected) {
            this.socket.emit('subscribeToGlobal');
        }
    }

    // Send custom events
    sendEvent(eventName, data) {
        if (this.socket && this.isConnected) {
            this.socket.emit(eventName, data);
        }
    }

    // Check connection status
    isSocketConnected() {
        return this.isConnected && this.socket && this.socket.connected;
    }
}

// Create a singleton instance
const socketService = new SocketService();

export default socketService;